import {
  TimetablePrintColorOrigins,
  TimetablePrintColorPositions,
  TimetablePrintLayoutOptions,
  TimetableReportType,
} from '@bp/planung-graphql-types';
import { Grid, GridRow, ModalBottomButtons } from '@bp/ui-components';
import { Formik } from 'formik';
import { t } from 'i18next';
import { Form } from 'react-router-dom';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  use_CreateTimetablePrintConfigsMutation,
  use_TimetablePrintConfigsQuery,
  use_UpdateTimetablePrintConfigsMutation,
} from '../../../types/planung-graphql-client-defs';
import { showErrorToast, showSuccessUpdateToast } from '../../../utils/toast';
import { TimetablePrintConfigurationBaseData } from '../components/TimetablePrintConfigurationBaseData';
import { TimetablePrintConfigurationGrid } from '../components/TimetablePrintConfigurationGrid';
import { useGetInitialTimetablePrintConfig } from '../useGetTimetablePrintConfig';

export type TimetablePrintBaseData = {
  color: string;
  colorPosition: TimetablePrintColorPositions;
  colorOrigin: TimetablePrintColorOrigins;
  printClasses: boolean;
  scaleText: boolean;
  exactMinute: boolean;
  printSubtitle: boolean;
  printLogo: boolean;
  largeFont: boolean;
  showSchoolWeeks: boolean;
  showCalendarWeeks: boolean;
  showWeekSchoolDays: boolean;
  timeHorizontal: boolean;
};

export enum TimetablePrintConfigurationOptions {
  printClasses = 'printClasses',
  scaleText = 'scaleText',
  exactMinute = 'exactMinute',
  printSubtitle = 'printSubtitle',
  printLogo = 'printLogo',
  largeFont = 'largeFont',
  showSchoolWeeks = 'showSchoolWeeks',
  showCalendarWeeks = 'showCalendarWeeks',
  showWeekSchoolDays = 'showWeekSchoolDays',
  timeHorizonal = 'timeHorizontal',
}

export enum TimetablePrintLayoutPositions {
  topLeft = 'topLeft',
  topCenter = 'topCenter',
  topRight = 'topRight',
  center = 'center',
  bottomLeft = 'bottomLeft',
  bottomCenter = 'bottomCenter',
  bottomRight = 'bottomRight',
}

export type TimetablePrintLayoutData = {
  topLeft: TimetablePrintLayoutOptions;
  topCenter: TimetablePrintLayoutOptions;
  topRight: TimetablePrintLayoutOptions;
  center: TimetablePrintLayoutOptions;
  bottomLeft: TimetablePrintLayoutOptions;
  bottomCenter: TimetablePrintLayoutOptions;
  bottomRight: TimetablePrintLayoutOptions;
};

export type TimetablePrintConfigurationFormType = TimetablePrintBaseData & TimetablePrintLayoutData;

type TimetablePrintConfigurationFormProps = {
  subType: TimetableReportType;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const TimetablePrintConfigurationForm = ({ subType, onLoad, onClose }: TimetablePrintConfigurationFormProps) => {
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const context = useMemorizedCacheTag('TIMETABLE_PRINT_CONFIG');

  const [, updateConfig] = use_UpdateTimetablePrintConfigsMutation();
  const [, createConfig] = use_CreateTimetablePrintConfigsMutation();

  const [{ data }] = use_TimetablePrintConfigsQuery({
    variables: {
      where: {
        type: subType,
      },
    },
    context,
    pause: !subType,
  });

  const current = data?.timetablePrintConfigs?.at(0);

  const { initialBaseData, initialLayoutData } = useGetInitialTimetablePrintConfig({ subType, current });

  const initialValues: TimetablePrintConfigurationFormType = { ...initialBaseData, ...initialLayoutData };

  async function handleSubmit(values: TimetablePrintConfigurationFormType) {
    onLoad(true);

    const data = {
      printClasses: values?.printClasses,
      scaleText: values?.scaleText,
      exactMinute: values?.exactMinute,
      printSubtitle: values?.printSubtitle,
      printLogo: values?.printLogo,
      largeFont: values?.largeFont,
      printColorOrigin: values?.colorOrigin,
      printColorPosition: values?.colorPosition,
      showSchoolWeeks: values?.showSchoolWeeks,
      showCalendarWeeks: values?.showCalendarWeeks,
      showWeekSchoolDays: values?.showWeekSchoolDays,
      timeHorizontal: values?.timeHorizontal,
      bottomCenter: values?.bottomCenter,
      bottomLeft: values?.bottomLeft,
      bottomRight: values?.bottomRight,
      center: values?.center,
      topLeft: values?.topLeft,
      topCenter: values?.topCenter,
      topRight: values?.topRight,
    };

    let result;

    if (current?.uuid) {
      result = await updateConfig({
        where: {
          uuid: current.uuid,
        },
        update: {
          ...data,
        },
      });
    } else {
      result = await createConfig({
        input: {
          type: subType,
          organization: {
            connect: {
              where: {
                node: {
                  uuid: organizationUuid,
                },
              },
            },
          },
          ...data,
        },
      });
    }

    if (result.error) {
      showErrorToast(result.error);
    } else {
      showSuccessUpdateToast();
    }

    onLoad(false);
    onClose();
  }

  return (
    <Formik<TimetablePrintConfigurationFormType> initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values, resetForm }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow spacingTop='none' spacingBottom='l'>
                <TimetablePrintConfigurationBaseData />
              </GridRow>
              <GridRow headline={t('timetable.configuration.positions')}>
                <TimetablePrintConfigurationGrid />
              </GridRow>
            </Grid>

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              submitButton={{
                text: t('common.save'),
                callback: async () => {
                  await handleSubmit(values);
                  resetForm();
                },
              }}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
